import React from 'react';
import {
  Alert,
  AlertActionLink,
  Button,
  Checkbox,
  Content,
  Divider,
  ExpandableSection,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Label,
  List,
  ListItem,
  TextInput,
  Title,
} from '@patternfly/react-core';
import { PencilAltIcon } from '@patternfly/react-icons';
import { useWizardContext } from '@patternfly/react-core';
import type { DkimSelectors } from '../../../types/models/DkimSelector';
import type { OrganizationDomain } from '../../../types/models/OrganizationDomain';
import type { DomainResourceType, DomainResourceTypeKey } from './domainResourceTypes';
import type { KeptReason, ResourceInventory, ReviewSummary } from './deleteResourcesWizardSelection';
import { typeState } from './deleteResourcesWizardSelection';

/** What each *Will be kept* chip says. Colour carries no meaning on its own — the text does. */
const KEPT_REASON: Record<KeptReason, { label: string; color: 'grey' | 'orange' }> = {
  kept_by_choice: { label: 'Kept by choice', color: 'grey' },
  type_unselected: { label: 'Type not selected', color: 'grey' },
  depends_on_kept: { label: 'Kept with its items', color: 'grey' },
  forced_kept: { label: 'Cannot be deleted', color: 'orange' },
  under_development: { label: 'Under development', color: 'orange' },
};

export interface DeleteResourcesReviewStepProps {
  domain: OrganizationDomain;
  types: readonly DomainResourceType[];
  inventory: ResourceInventory;
  review: ReviewSummary;
  /** Reasons the organization domain cannot be selected; empty when it can. */
  orgDomainBlockers: readonly string[];
  /** Cross-type rules the backend would reject; empty when the selection is coherent. */
  violations: readonly string[];
  deleteOrgDomain: boolean;
  onToggleOrgDomain: (checked: boolean) => void;
  confirmName: string;
  onConfirmNameChange: (value: string) => void;
  /** Whether the typed name matches — the footer gates on the same value. */
  confirmMatches: boolean;
  dkim: DkimSelectors | null;
  /** Step id per type, so an Edit link can jump back to the right step. */
  stepIdOf: (key: DomainResourceTypeKey) => string;
  selectionStepId: string;
  /**
   * A refusal or transport failure from the last submit. Shown here rather than on a result screen
   * because nothing was executed: the selection is still the user's to fix.
   */
  submitError: { title: string; message: string; details: string[] } | null;
  /** Offered only when retrying can plausibly help (a transport failure, not a refusal). */
  onRetrySubmit?: () => void;
}

/**
 * The review step: exactly what goes, exactly what stays, and the confirmation.
 *
 * Every registered type appears, in registry order, split into *Will be deleted* and *Will be kept*
 * with the consequence of keeping anything spelled out; types with nothing hosted collapse into one
 * line. The organization domain is asked about **here**, where the invariant is fully evaluable, and
 * the danger action is gated on typing the domain name. Plan §5.3.
 */
const DeleteResourcesReviewStep: React.FunctionComponent<DeleteResourcesReviewStepProps> = ({
  domain,
  types,
  inventory,
  review,
  orgDomainBlockers,
  violations,
  deleteOrgDomain,
  onToggleOrgDomain,
  confirmName,
  onConfirmNameChange,
  confirmMatches,
  dkim,
  stepIdOf,
  selectionStepId,
  submitError,
  onRetrySubmit,
}) => {
  const { goToStepById } = useWizardContext();
  const nothingToDelete = types.filter((type) => review.nothingToDelete.includes(type.key));
  const shown = types.filter((type) => !review.nothingToDelete.includes(type.key));
  const orgDomainSelectable = orgDomainBlockers.length === 0;
  const confirmTouched = confirmName.length > 0;

  return (
    <div>
      <Content component="p" className="pf-v6-u-mb-md">
        Check what is about to be deleted for <strong>{domain.name}</strong>. Everything under <em>Will be kept</em>{' '}
        stays exactly as it is.
      </Content>

      {shown.map((type) => {
        const going = review.willDelete.get(type.key) ?? [];
        const kept = review.willKeep.get(type.key) ?? [];
        // `goToStepById` refuses hidden steps, so only a visible type step gets a step link; the
        // others send the user back to the type selection.
        const hasOwnStep = typeState(inventory, type) === 'selectable' && going.length + kept.length > 0;
        const editTarget = hasOwnStep && going.length > 0 ? stepIdOf(type.key) : selectionStepId;
        return (
          <div key={type.key} className="pf-v6-u-mb-md">
            <Title headingLevel="h3" size="md">
              {type.label}{' '}
              <Button
                variant="link"
                isInline
                icon={<PencilAltIcon />}
                onClick={() => goToStepById(editTarget)}
                aria-label={`Change what is deleted in ${type.label}`}
              >
                Edit
              </Button>
            </Title>

            <Content component="p" className="pf-v6-u-mt-sm">
              {going.length === 0
                ? 'Nothing will be deleted.'
                : `${going.length} ${going.length === 1 ? 'item' : 'items'} will be deleted:`}
            </Content>
            {going.length > 0 && (
              <List aria-label={`${type.label} — will be deleted`}>
                {going.map((element) => (
                  <ListItem key={element.key}>
                    {element.label}
                    {element.action === 'revoke' && ' (revoked)'}
                    {element.action === 'unbind' && ' (binding only)'}
                    {element.kind === 'mail_domain' && dkim !== null && dkim.count > 0
                      ? `, including ${dkim.count} DKIM ${dkim.count === 1 ? 'key' : 'keys'} and the antispam link`
                      : ''}
                  </ListItem>
                ))}
              </List>
            )}

            {kept.length > 0 && (
              <>
                <Content component="p" className="pf-v6-u-mt-sm">
                  {`${kept.length} ${kept.length === 1 ? 'item' : 'items'} will be kept:`}
                </Content>
                <List aria-label={`${type.label} — will be kept`}>
                  {kept.map(({ element, why }) => (
                    <ListItem key={element.key}>
                      {element.label}{' '}
                      <Label isCompact color={KEPT_REASON[why].color}>
                        {KEPT_REASON[why].label}
                      </Label>
                      {element.blockedReason !== null && ` — ${element.blockedReason}`}
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            <Divider className="pf-v6-u-mt-md" />
          </div>
        );
      })}

      {violations.length > 0 && (
        <Alert variant="danger" isInline component="h3" title="This selection cannot be run" className="pf-v6-u-mb-md">
          <List aria-label="What has to change" isPlain>
            {violations.map((violation) => (
              <ListItem key={violation}>{violation}</ListItem>
            ))}
          </List>
        </Alert>
      )}

      {review.consequences.length > 0 && (
        <Alert variant="info" isInline component="h3" title="What keeping these means" className="pf-v6-u-mb-md">
          <List aria-label="Consequences of what is kept" isPlain>
            {review.consequences.map((sentence) => (
              <ListItem key={sentence}>{sentence}</ListItem>
            ))}
          </List>
        </Alert>
      )}

      {nothingToDelete.length > 0 && (
        <ExpandableSection
          toggleTextCollapsed={`Show ${nothingToDelete.length} resource ${nothingToDelete.length === 1 ? 'type' : 'types'} with nothing to delete`}
          toggleTextExpanded={`Hide ${nothingToDelete.length} resource ${nothingToDelete.length === 1 ? 'type' : 'types'} with nothing to delete`}
          isIndented
        >
          <List aria-label="Resource types with nothing hosted">
            {nothingToDelete.map((type) => (
              <ListItem key={type.key}>{type.label}</ListItem>
            ))}
          </List>
        </ExpandableSection>
      )}

      <Divider className="pf-v6-u-my-md" />

      <Checkbox
        id="delete-org-domain"
        name="delete-org-domain"
        label={`Also delete the organization domain ${domain.name}`}
        description="The organization domain record itself, including its verification and auto-adopt configuration. Members who joined through it stay in the organization; new sign-ins no longer join automatically."
        isChecked={deleteOrgDomain}
        isDisabled={!orgDomainSelectable}
        onChange={(_event, checked) => onToggleOrgDomain(checked)}
        body={
          <HelperText>
            <HelperTextItem>
              {orgDomainSelectable
                ? 'Everything Ancalagon hosts for this domain is selected, so the domain record can go too. The name becomes available to anyone again.'
                : `Kept because ${orgDomainBlockers.join(', and ')}. If any resource is kept, the organization domain is kept.`}
            </HelperTextItem>
          </HelperText>
        }
      />

      {submitError !== null && (
        <Alert
          variant="danger"
          isInline
          component="h3"
          title={submitError.title}
          className="pf-v6-u-mb-md"
          actionLinks={onRetrySubmit ? <AlertActionLink onClick={onRetrySubmit}>Try again</AlertActionLink> : undefined}
        >
          <p>{submitError.message}</p>
          {submitError.details.length > 0 && (
            <List aria-label="What the backend refused" isPlain>
              {submitError.details.map((detail) => (
                <ListItem key={detail}>{detail}</ListItem>
              ))}
            </List>
          )}
        </Alert>
      )}

      <Divider className="pf-v6-u-my-md" />

      {/* The wizard's own Next is a submit button, so this form must never submit natively. */}
      <Form onSubmit={(event) => event.preventDefault()}>
        <FormGroup label={`Type ${domain.name} to confirm`} fieldId="delete-resources-confirm" isRequired>
          <TextInput
            id="delete-resources-confirm"
            name="delete-resources-confirm"
            type="text"
            value={confirmName}
            onChange={(_event, value) => onConfirmNameChange(value)}
            validated={!confirmTouched ? 'default' : confirmMatches ? 'success' : 'error'}
            isRequired
            autoComplete="off"
            aria-describedby="delete-resources-confirm-helper"
          />
          <FormHelperText>
            <HelperText isLiveRegion>
              <HelperTextItem
                id="delete-resources-confirm-helper"
                variant={!confirmTouched ? 'default' : confirmMatches ? 'success' : 'error'}
              >
                {!confirmTouched
                  ? 'Deletion cannot be undone, so the domain name is typed out in full.'
                  : confirmMatches
                    ? 'The name matches.'
                    : `That is not ${domain.name}.`}
              </HelperTextItem>
            </HelperText>
          </FormHelperText>
        </FormGroup>
      </Form>
    </div>
  );
};

export default DeleteResourcesReviewStep;
